import { createBaseResponseSchema } from '@server/schemas'
import { HttpError } from '@server/shared/entry'
import { z } from 'zod'

import {
  getNextLectureInfoErrorResponseSchema,
  getNextLectureInfoResponseSchema,
  NOT_FOUND_NEXT_LECTURE_INFO_ERROR_CODE,
  NOT_FOUND_NEXT_LECTURE_INFO_ERROR_MESSAGE,
  NOT_FOUND_NEXT_LECTURE_INFO_ERROR_STATUS_CODE,
} from '../../model'

export const nextLectureInfoResponseSchema = createBaseResponseSchema(
  getNextLectureInfoResponseSchema._def.shape().result,
)
export const notFoundNextLectureInfoResponseSchema = createBaseResponseSchema(z.literal(null))

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

export const getNextLessonInfoAdapter = (
  response: unknown,
): z.infer<typeof notFoundNextLectureInfoResponseSchema> | z.infer<typeof nextLectureInfoResponseSchema> => {
  const normalizedNextLectureInfoResponse = nextLectureInfoResponseSchema.safeParse(response)

  if (normalizedNextLectureInfoResponse.success) {
    return normalizedNextLectureInfoResponse.data
  }

  if (isRecord(response) && response.resultCd === NOT_FOUND_NEXT_LECTURE_INFO_ERROR_STATUS_CODE) {
    return {
      resultCd: NOT_FOUND_NEXT_LECTURE_INFO_ERROR_STATUS_CODE,
      resultCdName: NOT_FOUND_NEXT_LECTURE_INFO_ERROR_CODE,
      data: null,
      message: NOT_FOUND_NEXT_LECTURE_INFO_ERROR_MESSAGE,
    } satisfies z.infer<typeof notFoundNextLectureInfoResponseSchema>
  }

  const errorResult = getNextLectureInfoErrorResponseSchema.safeParse(response)

  if (!errorResult.success) {
    const parsedGetNextLectureInfoResponse = getNextLectureInfoResponseSchema.parse(response)
    return {
      resultCd: parsedGetNextLectureInfoResponse.resultCd,
      resultCdName: 'OK',
      message: null,
      data: parsedGetNextLectureInfoResponse.result,
    } satisfies z.infer<typeof nextLectureInfoResponseSchema>
  }

  const { resultCd, resultMsg } = errorResult.data

  if (resultCd === NOT_FOUND_NEXT_LECTURE_INFO_ERROR_STATUS_CODE) {
    return {
      resultCd: NOT_FOUND_NEXT_LECTURE_INFO_ERROR_STATUS_CODE,
      resultCdName: NOT_FOUND_NEXT_LECTURE_INFO_ERROR_CODE,
      data: null,
      message: NOT_FOUND_NEXT_LECTURE_INFO_ERROR_MESSAGE,
    } satisfies z.infer<typeof notFoundNextLectureInfoResponseSchema>
  }

  throw HttpError.fromResponse(resultCd, {
    code: resultCd.toString(),
    message: resultMsg ?? 'Unknown error',
  })
}
